import axios from 'axios';
import { extractFromSlug } from 'helpers/utils';
import {
  PARTNER_UPCOMING_LIVE_CLASS_API,
  PARTNER_ENROLLED_LIVE_CLASS_API,
  PARTNER_ENROLL_LIVE_CLASS_API,
  PARTNER_RESCHEDULE_LIVE_CLASS_API,
  PARTNER_UNENROLL_LIVE_CLASS_API,
  PARTNER_ENTITLEMENTS_QUOTA_API,
} from 'config/constants';
import type { LiveVirtualClass } from 'types/liveClass';
import type { LmsClassEnrollment } from 'types/class';
import type { EnablementsQuotas } from 'types/user';

interface LvcListResponse {
  items: LiveVirtualClass[];
}

const { get, post } = axios;

export async function getPartnerOfferingUpcomingLiveClass(
  offeringSlug: string,
  userUuid: string,
) {
  try {
    const { code: offeringCode } = extractFromSlug(offeringSlug);
    if (!offeringCode) {
      throw new Error('offeringCode is required');
    }
    if (!userUuid) {
      throw new Error('userUuid is required');
    }

    const result = await get<LvcListResponse>(PARTNER_UPCOMING_LIVE_CLASS_API, {
      params: {
        offering_code: offeringCode,
        user_uuid: userUuid,
      },
    });

    return result.data;
  } catch (error) {
    throw new Error(error);
  }
}

export async function getPartnerOfferingEnrolledLiveClass(userUuid: string) {
  try {
    if (!userUuid) {
      throw new Error('userUuid is required');
    }

    const result = await get<LvcListResponse>(PARTNER_ENROLLED_LIVE_CLASS_API, {
      params: {
        user_uuid: userUuid,
      },
    });

    return result.data;
  } catch (error) {
    throw new Error(error);
  }
}

export async function getPartnerEntitlementsQuota(userUuid: string) {
  try {
    if (!userUuid) {
      throw new Error('userUuid is required');
    }

    const result = await get<EnablementsQuotas>(
      PARTNER_ENTITLEMENTS_QUOTA_API,
      {
        params: {
          user_uuid: userUuid,
        },
      },
    );

    return result.data;
  } catch (error) {
    throw new Error(error);
  }
}

export async function enrollPartnerLiveClass(
  offeringId: string,
  userUuid: string,
  offeringSlug: string,
) {
  try {
    const { code: offeringCode } = extractFromSlug(offeringSlug);
    if (!offeringId) {
      throw new Error('offeringId is required');
    }
    if (!userUuid) {
      throw new Error('userUuid is required');
    }
    if (!offeringCode) {
      throw new Error('offeringCode is required');
    }

    const result = await post<LmsClassEnrollment>(
      PARTNER_ENROLL_LIVE_CLASS_API.replace('<<offering_id>>', offeringId),
      undefined,
      {
        params: {
          user_uuid: userUuid,
          offering_code: offeringCode,
        },
      },
    );

    return result.data;
  } catch (error) {
    throw error;
  }
}

export async function reschedulePartnerLiveClass(
  offeringId: string,
  rescheduleFromOfferingId: string,
  userUuid: string,
  offeringSlug: string,
) {
  try {
    const { code: offeringCode } = extractFromSlug(offeringSlug);
    if (!offeringId || !rescheduleFromOfferingId) {
      throw new Error('offeringId and rescheduleFromOfferingId are required');
    }
    if (!userUuid) {
      throw new Error('userUuid is required');
    }
    if (!offeringCode) {
      throw new Error('offeringCode is required');
    }

    const result = await post<LmsClassEnrollment>(
      PARTNER_RESCHEDULE_LIVE_CLASS_API.replace(
        '<<offering_id>>',
        offeringId,
      ).replace('<<from_offering_id>>', rescheduleFromOfferingId),
      undefined,
      {
        params: {
          user_uuid: userUuid,
          offering_code: offeringCode,
        },
      },
    );

    return result.data;
  } catch (error) {
    throw error;
  }
}

export async function unenrollPartnerLiveClass(
  offeringId: string,
  userUuid: string,
  offeringSlug: string,
) {
  try {
    const { code: offeringCode } = extractFromSlug(offeringSlug);
    if (!offeringId) {
      throw new Error('offeringId is required');
    }
    if (!userUuid) {
      throw new Error('userUuid is required');
    }
    if (!offeringCode) {
      throw new Error('offeringCode is required');
    }

    const result = await post<LmsClassEnrollment>(
      PARTNER_UNENROLL_LIVE_CLASS_API.replace('<<offering_id>>', offeringId),
      undefined,
      {
        params: {
          user_uuid: userUuid,
          offering_code: offeringCode,
        },
      },
    );

    return result.data;
  } catch (error) {
    throw error;
  }
}
